import React from 'react';
import {
  VictoryContainer,
  VictoryPie,
  VictoryTooltip,
  VictoryTooltipProps,
} from 'victory';
import Legend from './Legend';
import ChartTitle from './ChartTitle';
import { EmptyState } from 'components/EmptyState/EmptyState';
import Skeleton from 'components/Skeleton/Skeleton';
import CustomSlice from './CustomSlice';
import Arrows from './Arrows';
import { useChart } from 'hooks/useChart';
import { useTheme } from 'styled-components';

export interface DonutChartData {
  x: string;
  y: number;
  backgroundColor: string;
}

interface DonutChartProps {
  data: DonutChartData[];
  title?: string;
  isLoading?: boolean;
  showTicketLabel?: boolean;
  scrollLegendOnPieHover?: boolean;
}

interface CustomTooltipProps extends VictoryTooltipProps {
  isActive?: (datum: DonutChartData) => boolean;
}

const CustomTooltip = (props: CustomTooltipProps) => {
  const isActive =
    props.isActive &&
    props.datum &&
    props.isActive(props.datum as DonutChartData);
  return <VictoryTooltip {...props} active={isActive || props.active} />;
};

const DonutChart: React.FC<DonutChartProps> = ({
  data,
  title,
  isLoading,
  showTicketLabel = true,
  scrollLegendOnPieHover = false,
}) => {
  const theme = useTheme();
  const {
    dataSet,
    handleArrowClick,
    hoveredDatum,
    isScrollable,
    scrollToLegendItem,
    setHoveredDatum,
    legendContainerRef,
    legendItemRefs,
    scrollDirection,
  } = useChart(data);

  return (
    <div>
      {title && <ChartTitle title={title} />}
      {isLoading ? (
        <Skeleton className="h-[170px] w-full" />
      ) : dataSet.length > 0 ? (
        <div className="flex w-full items-center justify-center gap-6">
          <div className="hidden sm:block">
            <VictoryPie
              padding={0}
              data={dataSet}
              innerRadius={120}
              padAngle={1}
              dataComponent={
                <CustomSlice
                  isHighlighted={({ x }: DonutChartData) =>
                    x === hoveredDatum?.x
                  }
                />
              }
              events={[
                {
                  target: 'data',
                  eventHandlers: {
                    onMouseOver: (_, dataComponentProps) => {
                      const label = dataComponentProps.datum.x;
                      scrollLegendOnPieHover && scrollToLegendItem(label);
                      return [
                        {
                          target: 'data',
                          mutation: () => {
                            setHoveredDatum(dataComponentProps.datum);
                            return { highlight: true };
                          },
                        },
                        {
                          target: 'labels',
                          mutation: () => ({ active: true }),
                        },
                      ];
                    },
                    onMouseOut: () => {
                      return [
                        {
                          target: 'data',
                          mutation: () => {
                            setHoveredDatum(undefined);
                            return { highlight: false };
                          },
                        },
                        {
                          target: 'labels',
                          mutation: () => ({ active: false }),
                        },
                      ];
                    },
                  },
                },
              ]}
              labels={({ datum }) =>
                showTicketLabel
                  ? `${datum.y} ${datum.y === 1 ? 'ticket' : 'tickets'}`
                  : `${datum.y}`
              }
              labelComponent={
                <CustomTooltip
                  isActive={({ x }) => x === hoveredDatum?.x}
                  cornerRadius={16}
                  pointerWidth={56}
                  pointerLength={24}
                  orientation="top"
                  style={{
                    fontFamily: theme.fonts.body,
                    fill: theme.colors.text.white,
                    fontWeight: theme.fontWeights.bold,
                    fontSize: '44px',
                  }}
                  flyoutPadding={54}
                  flyoutStyle={{
                    fill: theme.colors.background.baseSurface,
                    maxWidth: '120px',
                    borderRadius: '8px',
                    boxShadow:
                      '0px 1px 3px 0px rgba(0, 0, 0, 0.30), 0px 4px 8px 3px rgba(0, 0, 0, 0.15)',
                  }}
                />
              }
              cornerRadius={6}
              style={{
                data: {
                  fill: ({ datum }) => datum.backgroundColor,
                },
              }}
              containerComponent={
                <VictoryContainer className="[&>svg]:overflow-visible" />
              }
            />
          </div>
          <div className="relative max-h-[160px] w-full">
            <div
              className="vertical-scrollbar flex max-h-[160px] w-full flex-col items-start justify-start overflow-y-auto overflow-x-hidden scrollbar-none"
              ref={legendContainerRef}
            >
              {data.map((datum) => (
                <Legend
                  key={datum.x}
                  ref={(el) => (legendItemRefs.current[datum.x] = el)}
                  backgroundColor={datum.backgroundColor}
                  x={datum.x}
                  y={datum.y}
                  onMouseOver={() => setHoveredDatum(datum)}
                  onMouseOut={() => setHoveredDatum(undefined)}
                  onFocus={() => setHoveredDatum(datum)}
                  onBlur={() => setHoveredDatum(undefined)}
                  active={hoveredDatum?.x === datum.x}
                />
              ))}
            </div>
            {isScrollable && (
              <Arrows
                handleArrowClick={handleArrowClick}
                scrollDirection={scrollDirection}
              />
            )}
          </div>
        </div>
      ) : (
        <EmptyState />
      )}
    </div>
  );
};

export default DonutChart;
